import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronLeft, ChevronRight, Users, TrendingUp, Calendar, BarChart3, Settings, Target, LogOut, User, ChevronLeft as BackIcon, Navigation } from 'lucide-react';
import { LanguageToggle } from './LanguageToggle';
import supabase from '../lib/supabase';
import { OrgContext, OrgRow, OrgUser } from '../context/OrgContext';

const DEMO_EMAIL = import.meta.env.VITE_LAUNCH_DEMO_EMAIL || 'test1@victorlazari.com';
const DEMO_ORG_ID = import.meta.env.VITE_LAUNCH_DEMO_ORG_ID || '8e5b742a-0c6e-4217-9f69-5f3cf19103b6';
const DEMO_ORG_NAME = 'Launch Demo Org';
const DEMO_ORG_SLUG = 'launch-demo';

type UserRow = {
  id: string;
  email: string;
  full_name: string | null;
  role: 'owner' | 'admin' | 'agent' | 'viewer';
  org_id?: string | null;
};

export const CRMLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [me, setMe] = useState<UserRow | null>(null);
  const [org, setOrg] = useState<OrgRow | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // CRM is always in Russian: switch to 'ru' on mount, restore on unmount
  const [languageKey, setLanguageKey] = useState(i18n.language);
  useEffect(() => {
    const previousLng = i18n.language;
    if (previousLng !== 'ru') {
      i18n.changeLanguage('ru');
    }
    return () => {
      if (previousLng !== 'ru') {
        i18n.changeLanguage(previousLng);
      }
    };
  }, [i18n]);

  useEffect(() => {
    const handleLanguageChange = (lng: string) => setLanguageKey(lng);
    i18n.on('languageChanged', handleLanguageChange);
    window.addEventListener('languageChanged', () => setLanguageKey(i18n.language));
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
      window.removeEventListener('languageChanged', () => setLanguageKey(i18n.language));
    };
  }, [i18n]);

  const slugify = useCallback((value: string) => {
    return value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '')
      .slice(0, 48);
  }, []);

  const ensureOrgForUser = useCallback(async (user: UserRow): Promise<OrgRow | null> => {
    if (user.org_id) {
      const { data } = await supabase
        .from('organizations')
        .select('id,name,slug')
        .eq('id', user.org_id)
        .maybeSingle();
      return (data as OrgRow | null) ?? null;
    }

    const baseName = user.full_name || user.email?.split('@')[0] || 'Organization';
    const baseSlug = slugify(baseName.length ? baseName : 'org');

    let attempt = baseSlug || `org-${Math.random().toString(36).slice(2, 6)}`;
    let orgRow: OrgRow | null = null;

    for (let i = 0; i < 5; i += 1) {
      const { data, error } = await supabase
        .from('organizations')
        .insert({ name: `${baseName}'s Workspace`, slug: attempt })
        .select('id,name,slug')
        .maybeSingle();

      if (!error && data) {
        orgRow = data as OrgRow;
        break;
      }

      if (error && (error as any)?.code === '23505') {
        attempt = `${baseSlug}-${Math.random().toString(36).slice(2, 6)}`;
        continue;
      }

      if (!data) {
        const { data: existing } = await supabase
          .from('organizations')
          .select('id,name,slug')
          .eq('slug', attempt)
          .maybeSingle();
        if (existing) {
          orgRow = existing as OrgRow;
          break;
        }
      }
    }

    if (!orgRow) {
      const { data } = await supabase
        .from('organizations')
        .select('id,name,slug')
        .eq('slug', 'victor-lazari')
        .maybeSingle();
      orgRow = (data as OrgRow | null) ?? null;
    }

    if (orgRow) {
      await supabase.from('users').update({ org_id: orgRow.id }).eq('id', user.id);
    }

    return orgRow;
  }, [slugify]);

  const ensureDemoOrg = useCallback(async (user: UserRow): Promise<OrgRow | null> => {
    let orgRow: OrgRow | null = null;

    if (DEMO_ORG_ID) {
      const { data } = await supabase
        .from('organizations')
        .select('id,name,slug')
        .eq('id', DEMO_ORG_ID)
        .maybeSingle();
      orgRow = (data as OrgRow | null) ?? null;
    }

    if (!orgRow && DEMO_ORG_ID) {
      const { data } = await supabase
        .from('organizations')
        .upsert({ id: DEMO_ORG_ID, name: DEMO_ORG_NAME, slug: DEMO_ORG_SLUG }, { onConflict: 'id' })
        .select('id,name,slug')
        .maybeSingle();
      orgRow = (data as OrgRow | null) ?? null;
    }

    if (DEMO_ORG_ID && user.org_id !== DEMO_ORG_ID) {
      await supabase
        .from('users')
        .update({ org_id: DEMO_ORG_ID, role: user.role || 'viewer' })
        .eq('id', user.id);
    }

    return orgRow;
  }, []);

  const loadContext = useCallback(async () => {
    setLoading(true);
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) {
      setLoading(false);
      return;
    }

    const { data } = await supabase
      .from('users')
      .select('id,email,full_name,role,org_id')
      .eq('id', auth.user.id)
      .maybeSingle();

    const userRow = (data as (UserRow & { org_id: string | null }) | null) ?? null;
    if (!userRow) {
      setMe(null);
      setOrg(null);
      setLoading(false);
      return;
    }

    let orgRow: OrgRow | null = null;

    if (userRow.email === DEMO_EMAIL) {
      orgRow = await ensureDemoOrg(userRow);
    } else {
      orgRow = await ensureOrgForUser(userRow);
    }

    if (!orgRow && userRow.org_id) {
      const { data: existingOrg } = await supabase
        .from('organizations')
        .select('id,name,slug')
        .eq('id', userRow.org_id)
        .maybeSingle();
      orgRow = (existingOrg as OrgRow | null) ?? null;
    }

    setMe({
      id: userRow.id,
      email: userRow.email,
      full_name: userRow.full_name,
      role: userRow.role,
      org_id: orgRow?.id ?? userRow.org_id ?? null,
    } as UserRow);
    setOrg(orgRow);
    setLoading(false);
  }, [ensureDemoOrg, ensureOrgForUser]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate('/login');
  }

  const isAdmin = me && ['owner', 'admin'].includes(me.role);
  const canEdit = me && ['owner', 'admin', 'agent'].includes(me.role);

  const contextValue = useMemo(() => ({
    loading,
    org,
    orgId: org?.id ?? null,
    currentUser: me
      ? ({
          id: me.id,
          email: me.email,
          full_name: me.full_name,
          role: me.role,
          org_id: me.org_id ?? org?.id ?? null,
          canEdit,
        } as OrgUser)
      : null,
    refresh: loadContext,
  }), [loading, org, me, canEdit, loadContext]);

  // Helper to get short labels
  const getShortLabel = useCallback((label: string, maxLength: number = 8) => {
    if (label.length <= maxLength) return label;
    // Try to find a good break point
    const words = label.split(' ');
    if (words.length > 1 && words[0].length <= maxLength) {
      return words[0];
    }
    return label.substring(0, maxLength);
  }, []);

  const navItems = useMemo(() => [
    { path: '/crm/contacts', icon: Users, label: t('crm.navigation.contacts'), shortLabel: getShortLabel(t('crm.navigation.contacts')) },
    { path: '/crm/leads', icon: TrendingUp, label: t('crm.navigation.leads'), shortLabel: getShortLabel(t('crm.navigation.leads')) },
    { path: '/crm/prospezione', icon: Target, label: t('crm.navigation.prospezione'), shortLabel: getShortLabel(t('crm.navigation.prospezione')) },
    { path: '/crm/navigator', icon: Navigation, label: t('crm.navigation.navigator'), shortLabel: getShortLabel(t('crm.navigation.navigator')) },
    { path: '/crm/appointments', icon: Calendar, label: t('crm.navigation.appointments'), shortLabel: getShortLabel(t('crm.navigation.appointments')) },
    { path: '/crm/reports', icon: BarChart3, label: t('crm.navigation.reports'), shortLabel: getShortLabel(t('crm.navigation.reports')) },
    ...(isAdmin ? [{ path: '/crm/settings/users', icon: Settings, label: t('crm.navigation.settings'), shortLabel: getShortLabel(t('crm.navigation.settings')) }] : []),
  ], [t, isAdmin, i18n.language, getShortLabel]);

  // Main navigation items for bottom bar (first 5, plus settings if admin)
  const mainNavItems = useMemo(() => {
    const baseItems = navItems.slice(0, 5);
    // Add settings to mobile nav if admin
    if (isAdmin && navItems.length > 5) {
      return [...baseItems, navItems[navItems.length - 1]];
    }
    return baseItems;
  }, [navItems, isAdmin]);

  // Get current page title for breadcrumb
  const currentPageTitle = useMemo(() => {
    const currentItem = navItems.find(item => 
      location.pathname === item.path || location.pathname.startsWith(item.path + '/')
    );
    return currentItem?.label || '';
  }, [location.pathname, navItems]);

  // Check if we're on a detail page (has ID in path)
  const isDetailPage = useMemo(() => {
    return /\/crm\/(contacts|leads|appointments)\/[^/]+/.test(location.pathname);
  }, [location.pathname]);

  return (
    <div key={languageKey} className="fixed inset-0 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex overflow-hidden">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/60 dark:bg-black/80 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
          className={`
          fixed md:static inset-y-0 left-0 z-50
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          ${sidebarCollapsed ? 'md:w-20' : 'w-64'}
          border-r border-gray-200 dark:border-gray-700
          bg-gray-50 dark:bg-gray-800 flex flex-col
          transition-all duration-300 ease-in-out
        `}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          {!sidebarCollapsed && (
            <Link 
              to="/crm" 
              className="font-bold text-lg tracking-tight text-gray-900 dark:text-gray-100 truncate hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors group"
            >
              <span className="bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent group-hover:from-cyan-600 group-hover:to-cyan-500 dark:group-hover:from-cyan-400 dark:group-hover:to-cyan-300">
                {t('crm.navigation.brand')}
              </span>
            </Link>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden md:flex p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              aria-label={sidebarCollapsed ? t('crm.layout.expandSidebar') : t('crm.layout.collapseSidebar')}
            >
              {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="md:hidden p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              aria-label={t('crm.layout.closeMenu')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all
                  ${isActive
                    ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 border border-cyan-200 dark:border-cyan-700'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                  }
                  ${sidebarCollapsed ? 'justify-center' : ''}
                `}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-cyan-600 dark:text-cyan-400' : 'text-gray-500 dark:text-gray-400'}`} />
                {!sidebarCollapsed && (
                  <span className="text-sm font-medium truncate">{item.label}</span>
                )}
              </NavLink>
            );
          })}
          {mobileMenuOpen && (
            <div className="md:hidden pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
              <LanguageToggle variant="nav" className="w-full justify-center" />
            </div>
          )}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          {sidebarCollapsed ? (
            <div className="flex items-center justify-center">
              <img
                src="/images/Varienda BMS Icon.webp"
                alt="Varienda BMS"
                className="w-[42px] h-[42px] object-contain opacity-90 dark:opacity-70"
                loading="lazy"
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <span className="text-[11px] uppercase tracking-[0.35em] text-gray-500 dark:text-gray-400">{t('crm.layout.poweredBy')}</span>
              <img
                src="/images/varienda bms trasparent background.webp"
                alt="Varienda BMS"
                className="w-40 h-auto opacity-90 dark:opacity-70"
                loading="lazy"
              />
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 pb-16 md:pb-0">
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 px-4 py-3 md:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {isDetailPage && (
                <button
                  onClick={() => navigate(-1)}
                  className="md:hidden p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors flex-shrink-0"
                  aria-label={t('crm.layout.back')}
                >
                  <BackIcon className="w-5 h-5" />
                </button>
              )}
              {!isDetailPage && (
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors flex-shrink-0"
                  aria-label={t('crm.layout.openMenu')}
                >
                  <Menu className="w-5 h-5" />
                </button>
              )}
              {currentPageTitle && (
                <h1 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate md:hidden">
                  {currentPageTitle}
                </h1>
              )}
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <LanguageToggle variant="nav" className="hidden sm:flex" />
              <div className="hidden sm:block text-right">
                <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">{me?.full_name || me?.email}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{org?.name}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="sm:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400">
                  <User className="w-5 h-5" />
                </div>
                <button
                  onClick={handleLogout}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm flex items-center gap-2 text-gray-700 dark:text-gray-300"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('crm.navigation.logout')}</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6" style={{ maxWidth: '100%', width: '100%', boxSizing: 'border-box' }}>
          <OrgContext.Provider value={contextValue}>
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-sm text-gray-500">{t('crm.layout.loadingWorkspace')}</div>
              </div>
            ) : (
              children
            )}
          </OrgContext.Provider>
        </main>
      </div>

      {/* Bottom Navigation Bar - Mobile Only */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 shadow-lg" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="flex items-center justify-around px-1 py-1.5 max-w-screen-sm mx-auto">
          {mainNavItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            const Icon = item.icon;
            const isCompact = mainNavItems.length > 5;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  flex flex-col items-center justify-center gap-0.5 px-1 py-1.5 rounded-lg transition-all ${isCompact ? 'min-w-0 flex-1' : 'min-w-[56px] flex-1'}
                  active:scale-95
                  ${isActive
                    ? 'text-cyan-600 dark:text-cyan-400'
                    : 'text-gray-500 dark:text-gray-400 active:text-gray-700 dark:active:text-gray-300'
                  }
                `}
              >
                <div className={`relative ${isActive ? 'text-cyan-600 dark:text-cyan-400' : ''}`}>
                  <Icon className={`${isCompact ? 'w-4 h-4' : 'w-5 h-5'} mx-auto`} />
                  {isActive && (
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-cyan-600 dark:bg-cyan-400" />
                  )}
                </div>
                <span className={`${isCompact ? 'text-[8px]' : 'text-[10px]'} font-medium truncate w-full text-center leading-tight ${isActive ? 'text-cyan-600 dark:text-cyan-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {item.shortLabel}
                </span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default CRMLayout;



